import { useState } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  FileSpreadsheet, Plus, Download, CheckCircle, RotateCcw,
  ChevronDown, ChevronRight, Bot, AlertTriangle, Lock,
  Pencil, Trash2, Save, X,
} from 'lucide-react'
import { api } from '../lib/api'
import { useAIConsent } from '../components/shared/AIConsentModal'

// ── Design tokens ─────────────────────────────────────────────────────────────
const C = {
  bg:        'var(--color-bg)',
  card:      'var(--color-bg-card)',
  card2:     'var(--color-bg-card-2)',
  border:    'var(--color-border)',
  amber:     'var(--color-primary)',
  green:     '#4CAF50',
  red:       '#F87171',
  blue:      '#60A5FA',
  text:      'var(--color-text)',
  textMuted: 'var(--color-text-muted)',
  textFaint: 'var(--color-text-faint)',
}

// ── Types ─────────────────────────────────────────────────────────────────────

type ReportStatus = 'draft' | 'reviewed' | 'finalized'

interface OtherExpense {
  description: string
  amount_cents: number
}

interface AgReport {
  id: string
  tax_year: number
  status: ReportStatus
  // Part I: Income
  sales_livestock_cents: number
  sales_livestock_raised_cents: number
  coop_distributions_cents: number
  ag_program_payments_cents: number
  crop_insurance_cents: number
  custom_hire_income_cents: number
  other_income_cents: number
  gross_income_cents: number
  // Part II: Expenses
  exp_car_truck_cents: number
  exp_chemicals_cents: number
  exp_conservation_cents: number
  exp_custom_hire_cents: number
  exp_depreciation_cents: number
  exp_employee_benefits_cents: number
  exp_feed_cents: number
  exp_fertilizer_cents: number
  exp_freight_cents: number
  exp_fuel_cents: number
  exp_insurance_cents: number
  exp_interest_mortgage_cents: number
  exp_interest_other_cents: number
  exp_labor_cents: number
  exp_pension_cents: number
  exp_rent_vehicle_cents: number
  exp_rent_land_cents: number
  exp_repairs_cents: number
  exp_seeds_plants_cents: number
  exp_storage_cents: number
  exp_supplies_cents: number
  exp_taxes_cents: number
  exp_utilities_cents: number
  exp_vet_cents: number
  exp_other_cents: number
  total_expenses_cents: number
  net_farm_profit_cents: number
  other_expenses_detail: OtherExpense[]
  preparer_notes: string
  ai_analysis: string | null
  ai_analysis_at: string | null
  auto_populated_from: string
  last_synced_at: string | null
  finalized_at: string | null
  created_at: string
  updated_at: string
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function usd(cents: number): string {
  const neg = cents < 0
  const abs = Math.abs(cents)
  const str = `$${(abs / 100).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
  return neg ? `(${str})` : str
}

function centsFromDollar(s: string): number {
  const n = parseFloat(s.replace(/[^0-9.-]/g, ''))
  return isNaN(n) ? 0 : Math.round(n * 100)
}

function dollarFromCents(cents: number): string {
  return (cents / 100).toFixed(2)
}

const CURRENT_YEAR = new Date().getFullYear()

// ── Status badge ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: ReportStatus }) {
  const map: Record<ReportStatus, { label: string; color: string }> = {
    draft:     { label: 'Draft',     color: C.textMuted },
    reviewed:  { label: 'Reviewed',  color: C.blue },
    finalized: { label: 'Finalized', color: C.green },
  }
  const { label, color } = map[status]
  return (
    <span style={{
      fontSize: 11, fontWeight: 700, color, border: `1px solid ${color}33`,
      borderRadius: 4, padding: '2px 8px', letterSpacing: '0.5px',
    }}>
      {label.toUpperCase()}
    </span>
  )
}

// ── Dollar input ──────────────────────────────────────────────────────────────

function DollarInput({
  label, line, value, onChange, disabled,
}: {
  label: string; line: string; value: number; onChange: (v: number) => void; disabled?: boolean
}) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '6px 0', borderBottom: `1px solid ${C.border}` }}>
      <span style={{ fontSize: 11, color: C.textFaint, width: 54, flexShrink: 0 }}>{line}</span>
      <span style={{ flex: 1, fontSize: 13, color: C.textMuted }}>{label}</span>
      <input
        type="number"
        disabled={disabled}
        value={dollarFromCents(value)}
        min="0"
        step="0.01"
        onChange={e => onChange(centsFromDollar(e.target.value))}
        style={{
          width: 120, textAlign: 'right', background: disabled ? 'transparent' : C.card2,
          border: `1px solid ${disabled ? 'transparent' : C.border}`,
          borderRadius: 4, padding: '4px 8px', color: disabled ? C.textMuted : C.text,
          fontSize: 13,
        }}
      />
    </div>
  )
}

// ── Section expander ──────────────────────────────────────────────────────────

function Section({ title, open, onToggle, children }: {
  title: string; open: boolean; onToggle: () => void; children: React.ReactNode
}) {
  return (
    <div style={{ border: `1px solid ${C.border}`, borderRadius: 8, marginBottom: 12, overflow: 'hidden' }}>
      <button
        onClick={onToggle}
        style={{
          width: '100%', display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 16px', background: C.card, border: 'none', cursor: 'pointer',
          color: C.text, fontWeight: 700, fontSize: 13,
        }}
      >
        {open ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
        {title}
      </button>
      {open && <div style={{ padding: '12px 16px', background: C.card2 }}>{children}</div>}
    </div>
  )
}

// ── Report editor ─────────────────────────────────────────────────────────────

function ReportEditor({
  report,
  onClose,
}: {
  report: AgReport
  onClose: () => void
}) {
  const qc = useQueryClient()
  const [form, setForm] = useState<AgReport>({ ...report })
  const [openSections, setOpenSections] = useState<Record<string, boolean>>({
    income: true, expenses: false, other: false, notes: false
  })
  const [aiLoading, setAiLoading] = useState(false)
  const { requireConsent, ConsentModal } = useAIConsent('ai_tax_advice')

  const isFinalized = report.status === 'finalized'

  const toggle = (s: string) => setOpenSections(p => ({ ...p, [s]: !p[s] }))
  const setField = (k: keyof AgReport, v: number) => setForm(p => ({ ...p, [k]: v }))

  const saveMutation = useMutation({
    mutationFn: () => api.put<AgReport>(`/ag-reports/${report.id}`, form).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ag-reports'] }); onClose() },
  })

  const finalizeMutation = useMutation({
    mutationFn: () => api.post<AgReport>(`/ag-reports/${report.id}/finalize`, {}).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ag-reports'] }); onClose() },
  })

  const autoPopulateMutation = useMutation({
    mutationFn: () => api.post<{ report: AgReport }>(`/ag-reports/${report.id}/auto-populate`, {}).then(r => r.data),
    onSuccess: (data) => {
      setForm(data.report)
      qc.invalidateQueries({ queryKey: ['ag-reports'] })
    },
  })

  const handleAIAnalysis = async () => {
    try {
      await requireConsent()
      setAiLoading(true)
      const res = await api.post<{ analysis: string }>(`/ag-reports/${report.id}/ai-analyze`, {}).then(r => r.data)
      setForm(p => ({ ...p, ai_analysis: res.analysis }))
    } catch {
      // user declined consent or network error — no-op
    } finally {
      setAiLoading(false)
    }
  }

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <ConsentModal />
      <div style={{
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12,
        width: '95%', maxWidth: 760, maxHeight: '92vh', overflow: 'auto', padding: 24,
      }}>
        {/* Header */}
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <div>
            <h2 style={{ margin: 0, color: C.text, fontSize: 18 }}>
              Schedule F — Tax Year {report.tax_year}
            </h2>
            <div style={{ marginTop: 4, display: 'flex', gap: 8, alignItems: 'center' }}>
              <StatusBadge status={report.status} />
              <span style={{ fontSize: 11, color: C.textFaint }}>
                Source: {form.auto_populated_from === 'manual' ? 'Manual entry' : `Auto from ${form.auto_populated_from}`}
              </span>
            </div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textMuted }}>
            <X size={18} />
          </button>
        </div>

        {/* Toolbar */}
        {!isFinalized && (
          <div style={{ display: 'flex', gap: 8, marginBottom: 20, flexWrap: 'wrap' }}>
            <button
              onClick={() => autoPopulateMutation.mutate()}
              disabled={autoPopulateMutation.isPending}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                background: C.card2, border: `1px solid ${C.border}`, borderRadius: 6,
                color: C.text, cursor: 'pointer', fontSize: 13,
              }}
            >
              <RotateCcw size={13} />
              {autoPopulateMutation.isPending ? 'Syncing…' : 'Auto-populate from expenses'}
            </button>
            <button
              onClick={handleAIAnalysis}
              disabled={aiLoading}
              style={{
                display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px',
                background: `${C.amber}15`, border: `1px solid color-mix(in srgb, var(--color-primary) 27%, transparent)`, borderRadius: 6,
                color: C.amber, cursor: 'pointer', fontSize: 13,
              }}
            >
              <Bot size={13} />
              {aiLoading ? 'Analyzing…' : 'AI Tax Analysis'}
            </button>
          </div>
        )}

        {/* Part I: Farm Income */}
        <Section title="Part I — Farm Income" open={openSections.income} onToggle={() => toggle('income')}>
          <DollarInput label="Sales of livestock bought for resale" line="1a" value={form.sales_livestock_cents} onChange={v => setField('sales_livestock_cents', v)} disabled={isFinalized} />
          <DollarInput label="Sales of livestock/products raised" line="3a" value={form.sales_livestock_raised_cents} onChange={v => setField('sales_livestock_raised_cents', v)} disabled={isFinalized} />
          <DollarInput label="Cooperative distributions" line="5a" value={form.coop_distributions_cents} onChange={v => setField('coop_distributions_cents', v)} disabled={isFinalized} />
          <DollarInput label="Agricultural program payments" line="6a" value={form.ag_program_payments_cents} onChange={v => setField('ag_program_payments_cents', v)} disabled={isFinalized} />
          <DollarInput label="Crop insurance proceeds" line="7a" value={form.crop_insurance_cents} onChange={v => setField('crop_insurance_cents', v)} disabled={isFinalized} />
          <DollarInput label="Custom hire income" line="8" value={form.custom_hire_income_cents} onChange={v => setField('custom_hire_income_cents', v)} disabled={isFinalized} />
          <DollarInput label="Other farm income" line="9" value={form.other_income_cents} onChange={v => setField('other_income_cents', v)} disabled={isFinalized} />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', padding: '8px 0' }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Gross Farm Income (computed)</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.green }}>{usd(form.gross_income_cents)}</span>
          </div>
        </Section>

        {/* Part II: Farm Expenses */}
        <Section title="Part II — Farm Expenses" open={openSections.expenses} onToggle={() => toggle('expenses')}>
          <DollarInput label="Car and truck expenses" line="10" value={form.exp_car_truck_cents} onChange={v => setField('exp_car_truck_cents', v)} disabled={isFinalized} />
          <DollarInput label="Chemicals (pesticides, herbicides)" line="11" value={form.exp_chemicals_cents} onChange={v => setField('exp_chemicals_cents', v)} disabled={isFinalized} />
          <DollarInput label="Conservation expenses" line="12" value={form.exp_conservation_cents} onChange={v => setField('exp_conservation_cents', v)} disabled={isFinalized} />
          <DollarInput label="Custom hire (machine work)" line="13" value={form.exp_custom_hire_cents} onChange={v => setField('exp_custom_hire_cents', v)} disabled={isFinalized} />
          <DollarInput label="Depreciation" line="14" value={form.exp_depreciation_cents} onChange={v => setField('exp_depreciation_cents', v)} disabled={isFinalized} />
          <DollarInput label="Employee benefit programs" line="15" value={form.exp_employee_benefits_cents} onChange={v => setField('exp_employee_benefits_cents', v)} disabled={isFinalized} />
          <DollarInput label="Feed purchased" line="16" value={form.exp_feed_cents} onChange={v => setField('exp_feed_cents', v)} disabled={isFinalized} />
          <DollarInput label="Fertilizers and lime" line="17" value={form.exp_fertilizer_cents} onChange={v => setField('exp_fertilizer_cents', v)} disabled={isFinalized} />
          <DollarInput label="Freight and trucking" line="18" value={form.exp_freight_cents} onChange={v => setField('exp_freight_cents', v)} disabled={isFinalized} />
          <DollarInput label="Gasoline, fuel, oil" line="19" value={form.exp_fuel_cents} onChange={v => setField('exp_fuel_cents', v)} disabled={isFinalized} />
          <DollarInput label="Insurance (not health)" line="20" value={form.exp_insurance_cents} onChange={v => setField('exp_insurance_cents', v)} disabled={isFinalized} />
          <DollarInput label="Mortgage interest" line="21a" value={form.exp_interest_mortgage_cents} onChange={v => setField('exp_interest_mortgage_cents', v)} disabled={isFinalized} />
          <DollarInput label="Other interest" line="21b" value={form.exp_interest_other_cents} onChange={v => setField('exp_interest_other_cents', v)} disabled={isFinalized} />
          <DollarInput label="Labor hired" line="22" value={form.exp_labor_cents} onChange={v => setField('exp_labor_cents', v)} disabled={isFinalized} />
          <DollarInput label="Pension / profit sharing" line="23" value={form.exp_pension_cents} onChange={v => setField('exp_pension_cents', v)} disabled={isFinalized} />
          <DollarInput label="Rent/lease — vehicles & machinery" line="24a" value={form.exp_rent_vehicle_cents} onChange={v => setField('exp_rent_vehicle_cents', v)} disabled={isFinalized} />
          <DollarInput label="Rent/lease — land & buildings" line="24b" value={form.exp_rent_land_cents} onChange={v => setField('exp_rent_land_cents', v)} disabled={isFinalized} />
          <DollarInput label="Repairs and maintenance" line="25" value={form.exp_repairs_cents} onChange={v => setField('exp_repairs_cents', v)} disabled={isFinalized} />
          <DollarInput label="Seeds and plants purchased" line="26" value={form.exp_seeds_plants_cents} onChange={v => setField('exp_seeds_plants_cents', v)} disabled={isFinalized} />
          <DollarInput label="Storage and warehousing" line="27" value={form.exp_storage_cents} onChange={v => setField('exp_storage_cents', v)} disabled={isFinalized} />
          <DollarInput label="Supplies purchased" line="28" value={form.exp_supplies_cents} onChange={v => setField('exp_supplies_cents', v)} disabled={isFinalized} />
          <DollarInput label="Taxes" line="29" value={form.exp_taxes_cents} onChange={v => setField('exp_taxes_cents', v)} disabled={isFinalized} />
          <DollarInput label="Utilities" line="30" value={form.exp_utilities_cents} onChange={v => setField('exp_utilities_cents', v)} disabled={isFinalized} />
          <DollarInput label="Veterinary, breeding, medicine" line="31" value={form.exp_vet_cents} onChange={v => setField('exp_vet_cents', v)} disabled={isFinalized} />
          <DollarInput label="Other expenses (see below)" line="32a" value={form.exp_other_cents} onChange={v => setField('exp_other_cents', v)} disabled={isFinalized} />
          <div style={{ marginTop: 8, display: 'flex', justifyContent: 'space-between', padding: '8px 0', borderTop: `1px solid ${C.border}` }}>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Total Farm Expenses (computed)</span>
            <span style={{ fontSize: 13, fontWeight: 700, color: C.red }}>{usd(form.total_expenses_cents)}</span>
          </div>
        </Section>

        {/* Net profit */}
        <div style={{
          background: C.card, border: `1px solid ${C.border}`, borderRadius: 8,
          padding: '14px 16px', marginBottom: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center',
        }}>
          <div>
            <div style={{ fontSize: 13, fontWeight: 700, color: C.text }}>Line 34 — Net Farm Profit / (Loss)</div>
            <div style={{ fontSize: 11, color: C.textFaint, marginTop: 2 }}>Gross income minus total expenses</div>
          </div>
          <div style={{
            fontSize: 20, fontWeight: 700,
            color: form.net_farm_profit_cents >= 0 ? C.green : C.red,
          }}>
            {usd(form.net_farm_profit_cents)}
          </div>
        </div>

        {/* Notes / AI Analysis */}
        <Section title="Notes & AI Analysis" open={openSections.notes} onToggle={() => toggle('notes')}>
          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 4 }}>Preparer Notes</label>
            <textarea
              disabled={isFinalized}
              value={form.preparer_notes}
              onChange={e => setForm(p => ({ ...p, preparer_notes: e.target.value }))}
              rows={3}
              style={{
                width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 6,
                color: C.text, padding: '8px 10px', fontSize: 13, resize: 'vertical', boxSizing: 'border-box',
              }}
              placeholder="Add notes for your tax preparer…"
            />
          </div>
          {form.ai_analysis && (
            <div style={{ background: `${C.amber}0D`, border: `1px solid color-mix(in srgb, var(--color-primary) 20%, transparent)`, borderRadius: 8, padding: 14 }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 8 }}>
                <Bot size={14} style={{ color: C.amber }} />
                <span style={{ fontSize: 12, fontWeight: 700, color: C.amber }}>AI Tax Analysis</span>
                {form.ai_analysis_at && (
                  <span style={{ fontSize: 11, color: C.textFaint, marginLeft: 'auto' }}>
                    {new Date(form.ai_analysis_at).toLocaleDateString()}
                  </span>
                )}
              </div>
              <p style={{ margin: 0, fontSize: 13, color: C.textMuted, lineHeight: 1.6 }}>{form.ai_analysis}</p>
              <p style={{ margin: '10px 0 0', fontSize: 11, color: C.textFaint, fontStyle: 'italic' }}>
                For informational purposes only. Not tax advice. Consult a licensed tax professional.
              </p>
            </div>
          )}
        </Section>

        {/* Footer actions */}
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 20 }}>
          <button
            onClick={onClose}
            style={{
              padding: '8px 18px', background: 'none', border: `1px solid ${C.border}`,
              borderRadius: 6, color: C.textMuted, cursor: 'pointer', fontSize: 13,
            }}
          >
            Cancel
          </button>

          {!isFinalized && (
            <>
              <button
                onClick={() => saveMutation.mutate()}
                disabled={saveMutation.isPending}
                style={{
                  display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px',
                  background: C.amber, border: 'none', borderRadius: 6,
                  color: '#000', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                }}
              >
                <Save size={13} />
                {saveMutation.isPending ? 'Saving…' : 'Save Draft'}
              </button>

              {report.status !== 'draft' && (
                <button
                  onClick={() => {
                    if (window.confirm(`Finalize Schedule F for ${report.tax_year}? This cannot be undone.`)) {
                      finalizeMutation.mutate()
                    }
                  }}
                  disabled={finalizeMutation.isPending}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px',
                    background: C.green, border: 'none', borderRadius: 6,
                    color: '#000', cursor: 'pointer', fontSize: 13, fontWeight: 600,
                  }}
                >
                  <CheckCircle size={13} />
                  {finalizeMutation.isPending ? 'Finalizing…' : 'Finalize'}
                </button>
              )}
            </>
          )}

          <a
            href={`/api/v1/ag-reports/${report.id}/export.csv`}
            download={`schedule-f-${report.tax_year}.csv`}
            style={{
              display: 'flex', alignItems: 'center', gap: 6, padding: '8px 18px',
              background: C.card2, border: `1px solid ${C.border}`, borderRadius: 6,
              color: C.text, textDecoration: 'none', fontSize: 13,
            }}
          >
            <Download size={13} />
            Export CSV
          </a>
        </div>
      </div>
    </div>
  )
}

// ── Create report modal ───────────────────────────────────────────────────────

function CreateReportModal({ onClose }: { onClose: () => void }) {
  const qc = useQueryClient()
  const [year, setYear] = useState(CURRENT_YEAR - 1)

  const createMutation = useMutation({
    mutationFn: () => api.post<AgReport>('/ag-reports', { tax_year: year }).then(r => r.data),
    onSuccess: () => { qc.invalidateQueries({ queryKey: ['ag-reports'] }); onClose() },
  })

  return (
    <div style={{
      position: 'fixed', inset: 0, zIndex: 1000,
      background: 'rgba(0,0,0,0.75)', display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
      <div style={{
        background: C.bg, border: `1px solid ${C.border}`, borderRadius: 12,
        width: 360, padding: 24,
      }}>
        <h3 style={{ margin: '0 0 16px', color: C.text, fontSize: 16 }}>New Schedule F Report</h3>
        <label style={{ fontSize: 12, color: C.textMuted, display: 'block', marginBottom: 6 }}>Tax Year</label>
        <select
          value={year}
          onChange={e => setYear(Number(e.target.value))}
          style={{
            width: '100%', background: C.card, border: `1px solid ${C.border}`, borderRadius: 6,
            color: C.text, padding: '8px 10px', fontSize: 14, marginBottom: 20,
          }}
        >
          {Array.from({ length: 6 }, (_, i) => CURRENT_YEAR - i).map(y => (
            <option key={y} value={y}>{y}</option>
          ))}
        </select>
        <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
          <button onClick={onClose} style={{ padding: '8px 16px', background: 'none', border: `1px solid ${C.border}`, borderRadius: 6, color: C.textMuted, cursor: 'pointer', fontSize: 13 }}>
            Cancel
          </button>
          <button
            onClick={() => createMutation.mutate()}
            disabled={createMutation.isPending}
            style={{ padding: '8px 18px', background: C.amber, border: 'none', borderRadius: 6, color: '#000', cursor: 'pointer', fontSize: 13, fontWeight: 600 }}
          >
            {createMutation.isPending ? 'Creating…' : 'Create Report'}
          </button>
        </div>
      </div>
    </div>
  )
}

// ── Main page ─────────────────────────────────────────────────────────────────

export function AgExemptionPage() {
  const [showCreate, setShowCreate] = useState(false)
  const [editing, setEditing] = useState<AgReport | null>(null)

  const { data, isLoading } = useQuery<{ reports: AgReport[] }>({
    queryKey: ['ag-reports'],
    queryFn: () => api.get<{ reports: AgReport[] }>('/ag-reports').then(r => r.data),
  })

  const qc = useQueryClient()
  const deleteMutation = useMutation({
    mutationFn: (id: string) => api.delete<void>(`/ag-reports/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['ag-reports'] }),
  })

  const reports = data?.reports ?? []

  return (
    <main id="main-content" style={{ padding: 24, minHeight: '100dvh', background: C.bg }}>
      {/* Modals */}
      {showCreate && <CreateReportModal onClose={() => setShowCreate(false)} />}
      {editing && <ReportEditor report={editing} onClose={() => setEditing(null)} />}

      {/* Page header */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 4 }}>
            <FileSpreadsheet size={22} style={{ color: C.amber }} />
            <h1 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.text }}>
              Agricultural Exemption Reports
            </h1>
          </div>
          <p style={{ margin: 0, fontSize: 13, color: C.textMuted }}>
            IRS Schedule F — Profit or Loss from Farming. Auto-populated from your Grafty expense and harvest records.
          </p>
        </div>
        <button
          onClick={() => setShowCreate(true)}
          style={{
            display: 'flex', alignItems: 'center', gap: 6, padding: '9px 18px',
            background: C.amber, border: 'none', borderRadius: 8, color: '#000',
            cursor: 'pointer', fontSize: 14, fontWeight: 600, flexShrink: 0,
          }}
        >
          <Plus size={15} />
          New Report
        </button>
      </div>

      {/* Info banner */}
      <div style={{
        background: `${C.blue}0D`, border: `1px solid ${C.blue}33`, borderRadius: 8,
        padding: '12px 16px', marginBottom: 20, display: 'flex', gap: 10, alignItems: 'flex-start',
      }}>
        <AlertTriangle size={16} style={{ color: C.blue, flexShrink: 0, marginTop: 1 }} />
        <p style={{ margin: 0, fontSize: 13, color: C.textMuted }}>
          These reports are for your reference only. Always review with a licensed tax professional before filing.
          Amounts are auto-computed from line items; you can also enter values manually for any line.
        </p>
      </div>

      {/* Reports list */}
      {isLoading ? (
        <div style={{ color: C.textMuted, fontSize: 14, textAlign: 'center', padding: 60 }}>
          Loading reports…
        </div>
      ) : reports.length === 0 ? (
        <div style={{
          textAlign: 'center', padding: 60, border: `1px dashed ${C.border}`,
          borderRadius: 12, color: C.textFaint,
        }}>
          <FileSpreadsheet size={40} style={{ marginBottom: 12, opacity: 0.3 }} />
          <p style={{ margin: 0, fontSize: 14 }}>No reports yet. Create your first Schedule F report.</p>
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
          {reports.map(r => (
            <div
              key={r.id}
              style={{
                background: C.card, border: `1px solid ${C.border}`, borderRadius: 10,
                padding: '16px 20px', display: 'flex', alignItems: 'center', gap: 16,
              }}
            >
              {/* Year */}
              <div style={{ width: 72, flexShrink: 0 }}>
                <div style={{ fontSize: 22, fontWeight: 800, color: C.text }}>{r.tax_year}</div>
                <div style={{ fontSize: 11, color: C.textFaint }}>Tax Year</div>
              </div>

              {/* Status */}
              <StatusBadge status={r.status} />

              {/* Financials */}
              <div style={{ flex: 1, display: 'flex', gap: 24 }}>
                <div>
                  <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 2 }}>Gross Income</div>
                  <div style={{ fontSize: 14, color: C.green }}>{usd(r.gross_income_cents)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 2 }}>Total Expenses</div>
                  <div style={{ fontSize: 14, color: C.red }}>{usd(r.total_expenses_cents)}</div>
                </div>
                <div>
                  <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 2 }}>Net Profit / (Loss)</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: r.net_farm_profit_cents >= 0 ? C.green : C.red }}>
                    {usd(r.net_farm_profit_cents)}
                  </div>
                </div>
              </div>

              {/* Finalized lock */}
              {r.status === 'finalized' && (
                <span aria-label="Finalized — read only" title="Finalized — read only">
                  <Lock size={14} style={{ color: C.textFaint }} />
                </span>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 6 }}>
                <button
                  onClick={() => setEditing(r)}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
                    background: C.card2, border: `1px solid ${C.border}`, borderRadius: 6,
                    color: C.text, cursor: 'pointer', fontSize: 12,
                  }}
                >
                  <Pencil size={12} />
                  {r.status === 'finalized' ? 'View' : 'Edit'}
                </button>
                <a
                  href={`/api/v1/ag-reports/${r.id}/export.csv`}
                  download={`schedule-f-${r.tax_year}.csv`}
                  style={{
                    display: 'flex', alignItems: 'center', gap: 5, padding: '6px 12px',
                    background: C.card2, border: `1px solid ${C.border}`, borderRadius: 6,
                    color: C.text, textDecoration: 'none', fontSize: 12,
                  }}
                >
                  <Download size={12} />
                  CSV
                </a>
                {r.status !== 'finalized' && (
                  <button
                    onClick={() => {
                      if (window.confirm(`Delete Schedule F report for ${r.tax_year}?`)) {
                        deleteMutation.mutate(r.id)
                      }
                    }}
                    style={{
                      display: 'flex', alignItems: 'center', padding: '6px 8px',
                      background: 'none', border: `1px solid ${C.border}`, borderRadius: 6,
                      color: C.red, cursor: 'pointer',
                    }}
                    title="Delete report"
                  >
                    <Trash2 size={12} />
                  </button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </main>
  )
}
